import React, { Dispatch, ReactElement, SetStateAction, useState, FC } from 'react';
import { Alert, Button, ButtonGroup, ButtonToolbar, Col, Row } from 'react-bootstrap';

import ActionConfirmationModal from '../../../components/modals/ActionConfirmationModal/ActionConfirmationModal';
import CommentModal from '../../../components/modals/CommentModal/CommentModal';
import DeleteConfirmationModal from '../../../components/modals/DeleteConfirmationModal/DeleteConfirmationModal';
import RequestCIBAgreementModal from '../../../components/modals/RequestCIBAgreementModal/RequestCIBAgreementModal';
import PropertyDisplay from '../../../components/PropertyDisplay/PropertyDisplay';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import StatusText from '../../../components/StatusText/StatusText';
import { digitalCodesConstants } from '../../../constants/digitalCodesConstants';
import { platformFeatures } from '../../../constants/platformConstants';
import { useReferenceData, useUserProfile } from '../../../hooks/reduxHooks';
import { postWSPRequestAction, postROMSizeRequestAction } from '../../../services/digitalCodesService';
import { formatCurrency } from '../../../utils/currencyUtils';
import { dateFormat, formatDate } from '../../../utils/dateUtils';
import { displayStringForCIBPriceType, getGiBformat } from '../../../utils/digitalCodesUtils';
import { getStatusForPriceRequestAction } from '../../../utils/digitalCodesUtils';
import { createMessageForError, toasterNotify } from '../../../utils/toaster';
import { isFeatureActiveForPlatform } from '../../../utils/userUtils';
import DeclineModal from './DeclineModal';
import ExtendLimitModal from './ExtendLimitModal';
import ROMSizeReviewModal from './ROMSizeReviewModal';

import {
	getActivePriceRequest,
	getEffectiveDateForPriceRequest,
	getInProcessPriceRequest,
	getReviewOrApproveStatus,
	getCIBPermissions,
	isExtendLimitDisabled
} from './helpers';
import RequestActivityLog from './RequestActivityLog';

const { APPROVED } = digitalCodesConstants.priceRequestActions;
const { PENDING_REVIEW, REVIEWED } = digitalCodesConstants.priceRequestStatuses;

interface RequestDetailProps {
	heading?: string;
	cibRequest?: CIBPriceRequestData;
	readOnly?: boolean;
	declineReadOnly?: boolean;
	canApproverApprove?: boolean;
	canReviewerApprove?: boolean;
	canApproverDecline?: boolean;
	canReviewerDecline?: boolean;
	nextCIBDeclarationDate?: string | null;
	canRetract?: boolean;
	canComment?: boolean;
	canExtendLimit?: boolean;
	setOpenModal?: Dispatch<SetStateAction<RequestsModalModel>>;
	component?: ProductComponentData;
	isInternal?: boolean;
}
const RequestDetail: FC<RequestDetailProps> = ({
	heading,
	cibRequest,
	canApproverApprove,
	canReviewerApprove,
	canApproverDecline,
	canReviewerDecline,
	nextCIBDeclarationDate,
	canComment,
	canRetract,
	readOnly,
	declineReadOnly,
	setOpenModal,
	isInternal,
	component,
}: RequestDetailProps): ReactElement => {
	const canBeReviewerApproved = cibRequest?.request_status === PENDING_REVIEW;
	const canBeApproverApproved = cibRequest?.request_status === REVIEWED;
	const effectiveDate = cibRequest && getEffectiveDateForPriceRequest(cibRequest);
	const status = getStatusForPriceRequestAction(cibRequest, isInternal);
	const reviewOrApproveStatus = cibRequest ? getReviewOrApproveStatus(cibRequest) : undefined;

	return (
		<>
			<SectionTitle>{heading}</SectionTitle>
			{setOpenModal && (
				<ButtonToolbar className="mb-3">
					<ButtonGroup>
						{cibRequest?.price_type_code === digitalCodesConstants.priceTypeCodes.WHOLESALE && (
							<Button
								variant="outline-secondary"
								onClick={() =>
									setOpenModal({ type: 'AGREEMENT_MODAL', data: cibRequest })
								}
								disabled={cibRequest?.price_type_code !== digitalCodesConstants.priceTypeCodes.WHOLESALE}
							>
								View Wholesale Price Agreement
							</Button>
						)}
						{canComment && (
							<>
								<Button
									variant="outline-secondary"
									disabled={readOnly}
									onClick={() =>
										setOpenModal({ type: 'COMMENT_MODAL', data: cibRequest })
									}
								>
									Comment
								</Button>{' '}
							</>
						)}
					</ButtonGroup>{' '}
					{((canReviewerApprove && canBeReviewerApproved) ||
						(canApproverApprove && canBeApproverApproved)) && (
						<Button
							variant="success"
							disabled={readOnly}
							onClick={() => {
								const modalType =
									cibRequest?.price_type_code === digitalCodesConstants.priceTypeCodes.DL_SIZE &&
									reviewOrApproveStatus === 'REVIEW'
										? 'ROMSIZE_REVIEW_MODAL'
										: 'APPROVE_MODAL';
								setOpenModal({ type: modalType, data: cibRequest, componentData: component});
							}}
						>
							Approve
						</Button>
					)}{' '}
					{((canReviewerDecline && canBeReviewerApproved) ||
						(canApproverDecline && canBeApproverApproved)) && (
						<Button
							variant="danger"
							disabled={declineReadOnly}
							onClick={() =>
								setOpenModal({ type: 'DECLINE_MODAL', data: cibRequest })
							}
						>
							Decline
						</Button>
					)}{' '}
					{canRetract && cibRequest?.retractable && (
						<>
							<Button
								variant="danger"
								disabled={readOnly}
								onClick={() =>
									setOpenModal({ type: 'RETRACT_MODAL', data: cibRequest })
								}
							>
								Retract
							</Button>{' '}
						</>
					)}
				</ButtonToolbar>
			)}

			{cibRequest?.price_type_code && (
				<PropertyDisplay label="Code-in-Box Pricing Method">
					{displayStringForCIBPriceType(cibRequest?.price_type_code)}
				</PropertyDisplay>
			)}
			{cibRequest?.rom_size_gib && (
				<PropertyDisplay label="File Size">
					{getGiBformat(cibRequest?.rom_size_gib)}
				</PropertyDisplay>
			)}
			{cibRequest?.rom_size_rom_band && (
				<PropertyDisplay label="ROM Band">{cibRequest?.rom_size_rom_band}</PropertyDisplay>
			)}
			<PropertyDisplay label="Request Status">
				<StatusText
					variant={
						['PENDING_REVIEW', 'REVIEWED'].includes(status as string)
							? 'warning'
							: undefined
					}
				>
					{status}
				</StatusText>
			</PropertyDisplay>
			<PropertyDisplay label="Requested By">
				{cibRequest?.requester_user_name || cibRequest?.user_signature}
			</PropertyDisplay>
			<PropertyDisplay label="Date Requested">
				{cibRequest?.submitted_datetime != null ? (
					formatDate(cibRequest?.submitted_datetime, dateFormat.DATE)
				) : (
					<>&mdash;</>
				)}
			</PropertyDisplay>
			{effectiveDate && (
				<PropertyDisplay label="Effective Date">
					{formatDate(effectiveDate, dateFormat.DATE)}
				</PropertyDisplay>
			)}
			{status === 'APPROVED_AND_ACTIVE' && nextCIBDeclarationDate != null ? (
				<PropertyDisplay label="Next Declaration Date">
					{formatDate(nextCIBDeclarationDate, dateFormat.DATE)}
				</PropertyDisplay>
			) : null}
			<PropertyDisplay label="Price (USD)">
				{cibRequest?.certified_price != null ? (
					formatCurrency(cibRequest?.certified_price)
				) : (
					<>&mdash;</>
				)}
			</PropertyDisplay>
		</>
	);
};

interface CIBPriceRequestsTabProps {
	show?: boolean;
	nsUid?: string;
	token?: string;
	cibRequests?: (CIBPriceRequestData &
		Required<Pick<CIBPriceRequestData, 'actions'>>)[];
	canCreateCIBPriceRequest?: boolean;
	nextCIBDeclarationDate?: string | null;
	platformCode?: PlatformCode;
	component?: ProductComponentData;
	reloadComponentData: () => void;
	readOnly?: boolean;
	declineReadOnly?: boolean;
	rightsHolderId?: string;
	users?: Record<string, any>[];
	companies?: Record<string, any>[];
}
interface RequestsModalModel {
	type?:
		| 'AGREEMENT_MODAL'
		| 'NEW_AGREEMENT_MODAL'
		| 'APPROVE_MODAL'
		| 'DECLINE_MODAL'
		| 'RETRACT_MODAL'
		| 'EXTEND_LIMIT_MODAL'
		| 'ROMSIZE_REVIEW_MODAL'
		| 'COMMENT_MODAL';
	data?: CIBPriceRequestData;
	componentData?: ProductComponentData;
	cibRequestsData?: CIBPriceRequestData[];
	isSubmitting?: boolean;
}
const CIBPriceRequestsTab: FC<CIBPriceRequestsTabProps> = ({
	show,
	nsUid,
	token,
	cibRequests,
	canCreateCIBPriceRequest,
	nextCIBDeclarationDate,
	reloadComponentData,
	platformCode,
	component,
	readOnly,
	declineReadOnly,
	rightsHolderId,
	users,
	companies,
}: CIBPriceRequestsTabProps): ReactElement => {
	const platforms = useReferenceData('platforms');
	
	const userProfile: UserProfile = useUserProfile();
	const [openModal, setOpenModal] = useState<RequestsModalModel>({});

	const activeRequest =
		cibRequests && (getActivePriceRequest(cibRequests) as CIBPriceRequestData);
	const inProcessRequest =
		cibRequests && (getInProcessPriceRequest(cibRequests) as CIBPriceRequestData);
	const reviewOrApproveStatus = inProcessRequest
		? getReviewOrApproveStatus(inProcessRequest)
		: undefined;

	const {
		hasInternalPermission,
		canReviewerApprove,
		canApproverApprove,
		canRetract,
		canAgree,
		canComment,
		canExtendLimit,
	} = getCIBPermissions(userProfile);
	const isFeatureEnabled = platformCode != null && isFeatureActiveForPlatform(
		userProfile,
		platformFeatures.CODE_IN_BOX,
		platformCode,
		'edit',
	);

	const gameName = component?.game_name;

	const showNewRequestButton = !hasInternalPermission && isFeatureEnabled && canAgree;
	const showExtendLimitButton = !!activeRequest && isFeatureEnabled && canExtendLimit;

	return (
		<>
			{(showNewRequestButton || showExtendLimitButton) && (
				<ButtonToolbar>
					{showNewRequestButton && (
						<Button
							variant="primary"
							disabled={
								readOnly ||
								!component ||
								!cibRequests ||
								!rightsHolderId ||
								!canCreateCIBPriceRequest
							}
							onClick={() => setOpenModal({ type: 'NEW_AGREEMENT_MODAL' })}
						>
							Request New Code-in-Box Price Agreement
						</Button>
					)}
					{showExtendLimitButton && (
						<Button
							variant="outline-secondary"
							disabled={
								readOnly ||
								!cibRequests ||
								!rightsHolderId ||
								isExtendLimitDisabled(cibRequests, rightsHolderId)
							}
							onClick={() =>
								setOpenModal({
									type: 'EXTEND_LIMIT_MODAL',
									componentData: component,
									cibRequestsData: cibRequests,
								})
							}
						>
							Allow New Code-in-Box Price Agreement
						</Button>
					)}
				</ButtonToolbar>
			)}
			{inProcessRequest && (
				<RequestDetail
					heading="In Process"
					cibRequest={inProcessRequest}
					setOpenModal={setOpenModal}
					readOnly={readOnly}
					declineReadOnly={declineReadOnly}
					canReviewerApprove={isFeatureEnabled && canReviewerApprove}
					canApproverApprove={isFeatureEnabled && canApproverApprove}
					canApproverDecline={canApproverApprove}
					canReviewerDecline={canReviewerApprove}
					canRetract={canRetract}
					canComment={isFeatureEnabled && canComment}
					isInternal={hasInternalPermission}
				/>
			)}
			{activeRequest && (
				<RequestDetail
					heading="Active"
					cibRequest={activeRequest}
					setOpenModal={setOpenModal}
					readOnly={readOnly}
					canComment={isFeatureEnabled && canComment}
					isInternal={hasInternalPermission}
					nextCIBDeclarationDate={nextCIBDeclarationDate}
				/>
			)}
			<SectionTitle>Activity Log</SectionTitle>
			{cibRequests?.length ? (
				<div className="mx-3">
					<RequestActivityLog
						requests={cibRequests}
						showDetailsCallBack={(cibRequestId: number) => {
							const data = cibRequests?.find(
								(request) => request.cib_price_request_id === cibRequestId,
							);
							setOpenModal({ type: 'AGREEMENT_MODAL', data });
						}}
						users={users}
						companies={companies}
					/>
				</div>
			) : (
				<Alert variant="warning">
					No wholesale price activity exists on this component
				</Alert>
			)}
			<RequestCIBAgreementModal
				show={
					openModal?.type === 'AGREEMENT_MODAL' ||
					openModal?.type === 'NEW_AGREEMENT_MODAL'
				}
				closeModal={() => setOpenModal({})}
				readOnly={openModal?.type === 'AGREEMENT_MODAL' || undefined}
				requestId={
					openModal?.type === 'AGREEMENT_MODAL'
						? openModal?.data?.cib_price_request_id
						: undefined
				}
				productPlatformCode={
					openModal?.type === 'NEW_AGREEMENT_MODAL' ? platformCode : undefined
				}
				nsUid={openModal?.type === 'NEW_AGREEMENT_MODAL' ? nsUid : undefined}
				defaultValues={
					openModal?.type === 'NEW_AGREEMENT_MODAL'
						? {
								ns_uid: nsUid,
								company_name: userProfile.companyName,
								component_game_name: component?.game_name,
								product_game_code: component?.game_code,
								platform_name: platforms?.find(
									(platform: any) => platform.platform_code === platformCode,
								)?.platform_name,
						  }
						: {}
				}
				retailPrice={
					openModal?.type === 'NEW_AGREEMENT_MODAL'
						? component?.retail_prices[0]?.price
						: undefined
				}
				onSubmitted={() => {
					reloadComponentData();
				}}
				componentGameName={gameName}
				componentProductType={component?.product_type}
				componentContentType={component?.content_type}
			/>
			<ActionConfirmationModal
				show={openModal?.type === 'APPROVE_MODAL'}
				isSubmitting={openModal?.isSubmitting}
				onCancel={() => setOpenModal({})}
				onConfirm={async () => {
					setOpenModal({ ...openModal, isSubmitting: true } as RequestsModalModel);

					const cibRequest = openModal.data;
					if (cibRequest) {
						try {
							const actionFn =
								cibRequest.price_type_code ===
								digitalCodesConstants.priceTypeCodes.DL_SIZE
									? postROMSizeRequestAction
									: postWSPRequestAction;
							await actionFn(String(cibRequest.cib_price_request_id), {
								action: APPROVED,
							});
							toasterNotify(
								`Status has been updated to the code-in-box price request for "${gameName}"`,
								'info',
							);
							setOpenModal({});
						} catch (error: unknown) {
							error instanceof Error &&
								toasterNotify(
									createMessageForError(
										error,
										'approving the code-in-box price request',
									),
									'error',
									error,
								);
							setOpenModal({ ...openModal, isSubmitting: false });
						} finally {
							reloadComponentData();
						}
					}
				}}
				title="Approve Code-in-Box Price Request"
				confirmLabel="Approve"
				cancelLabel="Cancel"
			>
				<Alert variant="info">
					Do you wish to approve this Code-in-Box price request for "{gameName}"?{' '}
					{openModal.data?.requested_product_type && (
						<>
							Approving this request will also set the component's product type with
							the requested product type below.
						</>
					)}
				</Alert>
				<Row className="form-group">
					<Col sm={4} className="text-sm-right pt-0 form-label col-form-label col-sm-4">
						{openModal.data?.requested_product_type && (
							<span className="d-block">Requested</span>
						)}
						Product Type
					</Col>
					<Col sm={8} className="ml-sm-0 col-sm-8 my-auto">
						{
							platforms?.find(
								({ platform_code }) =>
									platform_code ===
									(openModal.data?.requested_product_type
										? openModal.data.requested_product_type
										: component?.product_type),
							)?.platform_name
						}
					</Col>
				</Row>
				{inProcessRequest?.price_type_code ===
					digitalCodesConstants.priceTypeCodes.DL_SIZE &&
					reviewOrApproveStatus === 'APPROVE' && (
						<>
							<Row className="form-group">
								<Col
									sm={4}
									className="text-sm-right pt-0 form-label col-form-label col-sm-4"
								>
									File Size
								</Col>
								<Col sm={8} className="ml-sm-0 col-sm-8 my-auto">
									{getGiBformat(inProcessRequest?.rom_size_gib ?? '')}
								</Col>
							</Row>
							<Row className="form-group">
								<Col
									sm={4}
									className="text-sm-right pt-0 form-label col-form-label col-sm-4"
								>
									ROM Band
								</Col>
								<Col sm={8} className="ml-sm-0 col-sm-8 my-auto">
									{inProcessRequest?.rom_size_rom_band}
								</Col>
							</Row>
						</>
					)}
			</ActionConfirmationModal>
			<DeclineModal
				show={openModal?.type === 'DECLINE_MODAL'}
				onClose={() => setOpenModal({})}
				onChange={() => reloadComponentData()}
				gameName={gameName}
				currentRequest={openModal?.data || undefined}
			/>
			<DeleteConfirmationModal
				show={openModal?.type === 'RETRACT_MODAL'}
				closeModal={() => setOpenModal({})}
				confirmDelete={async () => {
					setOpenModal({ ...openModal, isSubmitting: true });
					const currentCIBRequest = openModal.data;
					if (!currentCIBRequest) {
						return;
					}
					try {
						const actionFn =
							currentCIBRequest.price_type_code ===
							digitalCodesConstants.priceTypeCodes.DL_SIZE
								? postROMSizeRequestAction
								: postWSPRequestAction;
						await actionFn(currentCIBRequest.cib_price_request_id, {
							action: digitalCodesConstants.priceRequestActions.RETRACTED,
						});
						toasterNotify(
							`Your code-in-box price request for "${gameName}" has been retracted`,
							'info',
						);
						setOpenModal({});
					} catch (error) {
						toasterNotify(
							error instanceof Error
								? createMessageForError(
										error,
										'retracting the wholesale price request',
								  )
								: '',
							'error',
							(error as Error) || undefined,
						);
						setOpenModal({ ...openModal, isSubmitting: false });
					} finally {
						reloadComponentData();
					}
				}}
				title="Retract Code-in-Box Price Request"
				message={`Do you wish to retract the currently in process Code-in-Box price request for "${gameName}"?`}
				deleteLabel={'Retract'}
				submissionMessage={undefined}
				lastUpdated={undefined}
			/>
			<CommentModal
				show={openModal?.type === 'COMMENT_MODAL'}
				title="Add Comment on Code-in-Box Price Request"
				prompt=""
				onClose={() => setOpenModal({})}
				onChange={async (comment) => {
					try {
						if (openModal?.data?.cib_price_request_id) {
							const actionFn =
								openModal.data.price_type_code ===
								digitalCodesConstants.priceTypeCodes.DL_SIZE
									? postROMSizeRequestAction
									: postWSPRequestAction;
							await actionFn(openModal.data.cib_price_request_id, {
								action: 'COMMENT',
								comment,
							});
							setOpenModal({});
							toasterNotify('Comment posted on activity log', 'success');
						}
					} catch (error: unknown) {
						error instanceof Error &&
							toasterNotify(
								createMessageForError(
									error,
									'posting the comment on the code-in-box price request',
								),
								'error',
								error,
							);
						setOpenModal({ ...openModal });
					} finally {
						reloadComponentData();
					}
				}}
			/>
			<ExtendLimitModal
				show={openModal?.type === 'EXTEND_LIMIT_MODAL'}
				onClose={() => setOpenModal({})}
				onChange={() => reloadComponentData()}
				component={openModal?.componentData || undefined}
				priceRequests={cibRequests}
				rightsHolderId={rightsHolderId}
			/>
			<ROMSizeReviewModal
				show={openModal?.type === 'ROMSIZE_REVIEW_MODAL'}
				onClose={() => setOpenModal({})}
				onChange={() => reloadComponentData()}
				component={component || undefined}
				priceRequest={inProcessRequest}
			/>
		</>
	);
};

export default CIBPriceRequestsTab;
